/* ============================================================================
   Slide 7c — "The Eight Limbs" — SHARED CONTENT (single source of truth)
   Read by both EightLimbsInner (desktop) and HTLDSlide7cMobile. Edit copy ONLY
   here — English and German side by side; the German object must have exactly
   the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent7c = "#38b5b5";

export interface Seg { t: string; b?: boolean; em?: boolean; }

export interface Slide7cLimb { n: string; san: string; desc: string; }

/** Index at/after which the limb number is drawn in the accent colour. */
export const slide7cAccentFrom = 4;

const en = {
  meta: {
    breadcrumbPre: "The Foundation",
    breadcrumbAccent: "Preparing body and mind",
    headerTitlePlain: "The Eight ",
    headerTitleAccent: "Limbs",
    sanskritOverline: "अष्टांग योग · ASHTANGA",
    titlePre: "A Sattvic Life, ",
    titleAccentWord: "a Clear Mind",
    titlePost: "",
    subtitle: "The yogic foundation — right food, thought and rest — that steadies body and mind for the work ahead",
    calloutLabel: "Why this comes first",
    attribution: "As set out by Patañjali in the Yoga Sūtras",
  },
  limbs: [
    { n: "1 · Yama", san: "Ethical restraint",
      desc: "Ahimsa (non-violence), Satya (truth), Asteya (non-stealing), Brahmacharya (moderation), Aparigraha (non-grasping) — how we meet the world, leaving a clear conscience and an unburdened mind." },
    { n: "2 · Niyama", san: "Personal observance",
      desc: "Saucha (purity — sattvic food & clean surroundings), Santosha (contentment & positive thought), Tapas (discipline & regular rest), Svadhyaya (self-study), Ishvara Pranidhana (surrender) — the inner habits of a calm, ordered life." },
    { n: "3 · Asana", san: "Posture",
      desc: "A steady, supple body that can rest in stillness, without restlessness or discomfort pulling awareness back." },
    { n: "4 · Pranayama", san: "Breath",
      desc: "Conscious regulation of the breath gathers prana and clears the mind — steadying the breath steadies thought itself." },
    { n: "5 · Pratyahara", san: "Sense withdrawal",
      desc: "Drawing awareness inward, away from outer distraction — the bridge from body to mind, and the very turning-inward Dreamfulness asks for." },
    { n: "6 · Dharana", san: "Concentration",
      desc: "Holding attention gently on a single point, returning each time it wanders, without strain." },
    { n: "7 · Dhyana", san: "Meditation",
      desc: "Concentration become effortless — a seamless, sustained flow of awareness. The witness, trained." },
    { n: "8 · Samadhi", san: "Absorption",
      desc: "Complete absorption; the knower and the known dissolve into pure awareness." },
  ] as Slide7cLimb[],
  intro: [
    { t: "Yoga is far more than posture. Ashtanga — the " },
    { t: "eight limbs", b: true },
    { t: " — maps a whole way of living, from how we treat others to the quiet depths of meditation. Each limb prepares the next." },
  ] as Seg[],
  callout: [
    { t: "The early limbs purify and steady — right food, right thought, right rest. And " },
    { t: "Pratyahara, Dharana and Dhyana are the very faculties Dreamfulness draws on", b: true },
    { t: " — turning inward, holding attention, remaining aware. A grounded life is the soil in which conscious dreaming takes root." },
  ] as Seg[],
  canvas: {
    caption: "eight limbs, one path",
  },
};

export type Slide7cContent = typeof en;

const de: Slide7cContent = {
  meta: {
    breadcrumbPre: "Das Fundament",
    breadcrumbAccent: "Körper und Geist vorbereiten",
    headerTitlePlain: "Die acht ",
    headerTitleAccent: "Glieder",
    sanskritOverline: "अष्टांग योग · ASHTANGA",
    titlePre: "Ein sattvisches Leben, ",
    titleAccentWord: "ein klarer Geist",
    titlePost: "",
    subtitle: "Das yogische Fundament – rechte Nahrung, Gedanken und Ruhe – festigt Körper und Geist",
    calloutLabel: "Warum dies zuerst kommt",
    attribution: "Nach Patañjalis Yoga-Sūtras",
  },
  limbs: [
    { n: "1 · Yama", san: "Ethische Disziplin",
      desc: "Ahimsa (Gewaltlosigkeit), Satya (Wahrhaftigkeit), Asteya (Nicht-Stehlen), Brahmacharya (Maßhalten), Aparigraha (Nicht-Anhaften) – wie wir der Welt begegnen: mit reinem Gewissen und unbeschwertem Geist." },
    { n: "2 · Niyama", san: "Persönliche Übung",
      desc: "Saucha (Reinheit – sattvische Kost, saubere Umgebung), Santosha (Zufriedenheit, positives Denken), Tapas (Disziplin, feste Ruhezeiten), Svadhyaya (Selbststudium), Ishvara Pranidhana (Hingabe) – innere Gewohnheiten eines ruhigen Lebens." },
    { n: "3 · Asana", san: "Haltung",
      desc: "Ein stabiler, geschmeidiger Körper, der in Stille ruhen kann, ohne dass Unruhe oder Unbehagen die Aufmerksamkeit zurückholen." },
    { n: "4 · Pranayama", san: "Atem",
      desc: "Bewusste Atemlenkung sammelt Prana und klärt den Geist – ein ruhiger Atem beruhigt das Denken selbst." },
    { n: "5 · Pratyahara", san: "Rückzug der Sinne",
      desc: "Die Aufmerksamkeit nach innen lenken, fort von äußerer Ablenkung – die Brücke vom Körper zum Geist und genau die Innenwendung, die Dreamfulness braucht." },
    { n: "6 · Dharana", san: "Konzentration",
      desc: "Die Aufmerksamkeit sanft auf einen Punkt richten und mühelos zurückkehren, wann immer sie abschweift." },
    { n: "7 · Dhyana", san: "Meditation",
      desc: "Konzentration, die mühelos geworden ist – ein ununterbrochener Fluss des Gewahrseins. Der geschulte Zeuge." },
    { n: "8 · Samadhi", san: "Versenkung",
      desc: "Völlige Versenkung: Erkennender und Erkanntes werden reines Gewahrsein." },
  ],
  intro: [
    { t: "Yoga ist weit mehr als Körperhaltung. Ashtanga – die " },
    { t: "acht Glieder", b: true },
    { t: " – beschreibt einen ganzen Lebensweg, vom Umgang mit anderen bis in die stillen Tiefen der Meditation. Jedes Glied bereitet das nächste vor." },
  ],
  callout: [
    { t: "Die ersten Glieder reinigen und festigen – rechte Nahrung, rechtes Denken, rechte Ruhe. Und " },
    { t: "Pratyahara, Dharana und Dhyana sind genau die Fähigkeiten, auf die Dreamfulness baut", b: true },
    { t: " – nach innen wenden, Aufmerksamkeit halten, gewahr bleiben. Ein geerdetes Leben ist der Boden, in dem bewusstes Träumen wurzelt." },
  ],
  canvas: {
    caption: "acht Glieder, ein Weg",
  },
};

export const slide7cContent: ByLang<Slide7cContent> = { en, de };
export const useSlide7c = () => useL(slide7cContent);
